import React, { useEffect, useState } from 'react';
import { Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useRoute, type RouteProp } from '@react-navigation/native';
import { useNav, type AllParams } from '../navigation';
import { useAccount } from '../store/account';
import { checkoutAvailable, loadCatalog, preferredCurrency, submitCheckout } from '../platform/checkout';
import type { CheckoutCatalog } from '../types';
import { Body, Button, Caption, ListItem, Screen } from '../components/ui';
import Sheet from '../components/Sheet';
import BrandLogo from '../components/BrandLogo';
import EmbeddedPayment from '../platform/EmbeddedPayment';
import Icon from '../components/Icon';
import LegalLinks from '../components/LegalLinks';
import { font, radius, spacing, usePalette } from '../theme';

export default function CheckoutScreen() {
  const { t, i18n } = useTranslation();
  const p = usePalette();
  const nav = useNav();
  const params = useRoute<RouteProp<AllParams, 'Checkout'>>().params;
  const user = useAccount(s => s.user);
  const [currency, setCurrency] = useState(params?.currency ?? preferredCurrency());
  const [catalog, setCatalog] = useState<CheckoutCatalog | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(false);
  const [payment, setPayment] = useState<{ clientSecret: string; publishableKey: string } | null>(null);
  useEffect(() => { setPayment(null); }, [currency, user?.id]);
  const [currencyOpen, setCurrencyOpen] = useState(false);
  const [reload, setReload] = useState(0);
  useEffect(() => {
    if (!checkoutAvailable) return;
    let mounted = true;
    setCatalog(null);
    loadCatalog().then(c => {
      if (!mounted) return;
      setCatalog(c);
      setCurrency(current => c.billing ? c.billing.currency : c.currencies.some(x => x.code === current) ? current : 'eur');
      setLoadError(false);
    }).catch(() => { if (mounted) setLoadError(true); });
    return () => { mounted = false; };
  }, [reload, user?.id]);
  const selected = catalog?.offers.find(o => o.key === 'super');
  const divisor = catalog?.currencies.find(c => c.code === currency)?.divisor ?? 100;
  const money = (amount: number) => new Intl.NumberFormat(i18n.language, { style: 'currency', currency: currency.toUpperCase() }).format(amount / divisor);
  const amount = selected?.amounts[currency];
  const changing = user?.super?.role === 'owner' && user.super.source === 'stripe';
  return (
    <Screen contentStyle={{ width: '100%', maxWidth: 760, alignSelf: 'center', gap: spacing.xxl }}>
      <View style={{ gap: spacing.s, paddingTop: spacing.l }}>
        <BrandLogo size={96} />
        <Text style={{ fontSize: font.large, fontWeight: '800', color: p.text }}>{t('checkout.heading')}</Text>
      </View>
      {!checkoutAvailable ? <><Body>{t('checkout.unavailable')}</Body><Button label={t('checkout.back')} onPress={() => nav.navigate('Super')} /></> : loadError ? (
        <><Body>{t('checkout.loadError')}</Body><Button label={t('checkout.retry')} onPress={() => setReload(v => v + 1)} /></>
      ) : !catalog ? <BrandLogo loading label={t('super.loading')} /> : !catalog.offers.length ? <Body>{t('super.unavailable')}</Body> : (
        <>
          <View style={{ alignItems: 'flex-start', gap: spacing.m }}>
            <Text style={{ fontSize: font.large, fontWeight: '800', color: p.text }}>{amount !== undefined ? money(amount) : '—'}</Text>
            <Caption>{t('checkout.billedMonthly')}</Caption>
            <Button label={`${t('checkout.currency')} · ${currency.toUpperCase()}`} icon="country" variant="secondary" disabled={!!catalog.billing} onPress={() => setCurrencyOpen(true)} />
          </View>
          <View style={{ gap: spacing.m }}>
            <Text style={{ color: p.text, fontSize: font.headline, fontWeight: '700' }}>{t('checkout.benefits')}</Text>
            {(['checkout.benefit1', 'checkout.benefit2', 'checkout.benefit3'] as const).map(k => <View key={k} style={{ flexDirection: 'row', gap: spacing.s }}><Icon name="check" color={p.tint} size={18} /><Body secondary>{t(k)}</Body></View>)}
          </View>
          <View style={{ padding: spacing.xl, borderRadius: radius.l, backgroundColor: p.card, gap: spacing.l }}>
            <Caption>{t('checkout.summary')}</Caption>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', gap: spacing.l }}><Body>{t(changing ? 'checkout.newPrice' : 'checkout.total')}</Body><Text style={{ color: p.text, fontSize: font.title2, fontWeight: '800' }}>{amount !== undefined ? money(amount) : '—'}</Text></View>
            <Caption>{t('checkout.billedMonthly')}</Caption>
            <Caption>{t('checkout.taxIncluded')}</Caption>
            {changing ? <Caption>{t('checkout.proration')}</Caption> : null}
            {user && payment ? <EmbeddedPayment {...payment} onComplete={() => nav.replace('Super', { paid: '1' })} /> : user ? <Button big label={t(changing ? 'checkout.change' : 'checkout.continue')} icon="lock" loading={busy} disabled={!selected || amount === undefined || (changing && !catalog.billing)} onPress={async () => {
              if (!selected) return;
              setBusy(true); setError(false);
              try {
                const result = await submitCheckout(selected, currency);
                if (result === true) nav.replace('Super', { paid: '1' });
                else if (result) setPayment(result);
              }
              catch { setError(true); }
              finally { setBusy(false); }
            }} /> : <><Caption>{t('checkout.signInNote')}</Caption><Button label={t('checkout.signIn')} onPress={() => nav.navigate('Account')} /></>}
            {error ? <Caption color={p.danger}>{t('checkout.error')}</Caption> : null}
            <Caption>{t('checkout.paymentNote')}</Caption>
          </View>
          <LegalLinks pages={['terms', 'refunds', 'privacy']} />
          <Sheet visible={currencyOpen} onClose={() => setCurrencyOpen(false)} title={t('checkout.chooseCurrency')}>
            {catalog.currencies.map(c => <ListItem key={c.code} title={c.code.toUpperCase()} selected={currency === c.code} onPress={() => { setCurrency(c.code); setCurrencyOpen(false); }} />)}
          </Sheet>
        </>
      )}
    </Screen>
  );
}
